import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Project { id: string; code: string; name: string; status: string; }
interface ActivityEntry { id: string; icon: string | null; text: string; userName: string | null; time: string; }

interface Props {
  onClose: () => void;
  record: {
    id: string;
    code?: string | null;
    workerName: string;
    projectId: string;
    projectName: string | null;
    type: 'in' | 'out';
    timestamp: string;
  };
}

export function SigninCorrectModal({ onClose, record }: Props) {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState(record.projectId);
  const [type, setType] = useState(record.type);
  const iso = new Date(record.timestamp).toISOString().slice(0, 16);
  const [timestamp, setTimestamp] = useState(iso);
  const [reason, setReason] = useState('');
  const [reasonErr, setReasonErr] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });

  // Audit trail: pull recent activity-log entries that reference this record's id
  const { data: activity = [] } = useQuery({
    queryKey: ['activity-log', 'signin', record.id],
    queryFn: () => api.get<ActivityEntry[]>('/activity-log?limit=200').then((r) => r.data).catch(() => [] as ActivityEntry[]),
  });
  const auditEntries = useMemo(
    () => activity.filter((a) => a.text && (a.text.includes(record.id) || (record.code && a.text.includes(record.code)))),
    [activity, record.id, record.code],
  );

  const prettyText = (raw: string): string => {
    const ref = record.code || record.id;
    let out = raw;
    out = out.replaceAll(record.id, ref);
    if (!out.includes(record.workerName) && /Sign-in record \S+/.test(out)) {
      out = out.replace(/(Sign-in record \S+)/, `$1 (${record.workerName})`);
    }
    return out;
  };

  const save = useMutation({
    mutationFn: async () => api.patch(`/signin-records/${record.id}`, {
      projectId,
      type,
      timestamp: new Date(timestamp).toISOString(),
      correctionReason: reason.trim(),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signin-records'] });
      qc.invalidateQueries({ queryKey: ['activity-log'] });
      toast.success('Record corrected', record.workerName);
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.message ?? 'Endpoint may not yet support edits — check API'),
  });

  const del = useMutation({
    mutationFn: async () => api.delete(`/signin-records/${record.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signin-records'] });
      qc.invalidateQueries({ queryKey: ['activity-log'] });
      toast.info('Record deleted');
      onClose();
    },
    onError: (e: any) => toast.error('Delete failed', e?.response?.data?.message ?? 'Try again'),
  });

  const handleSave = () => {
    if (!reason.trim()) {
      setReasonErr(true);
      toast.warn('Reason required', 'Add a short note for the audit trail before saving.');
      return;
    }
    save.mutate();
  };

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 560, maxWidth: '98vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">✏ Correct Sign-In Record</div>
        <div className="modal-sub">{record.workerName}</div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Project</label>
            <select className="form-select" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Type</label>
            <select className="form-select" value={type} onChange={(e) => setType(e.target.value as 'in' | 'out')}>
              <option value="in">✅ In</option>
              <option value="out">🚪 Out</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Date &amp; Time</label>
          <input type="datetime-local" className="form-input" value={timestamp} onChange={(e) => setTimestamp(e.target.value)} />
        </div>
        <div className="form-row">
          <label className="form-label">
            Reason for correction <span style={{ color: 'var(--danger)' }}>*</span>{' '}
            <span style={{ color: 'var(--muted)', fontSize: 10, fontWeight: 400 }}>— recorded to audit trail</span>
          </label>
          <textarea
            className="form-textarea"
            placeholder="e.g. Worker forgot to sign out, project was miskeyed…"
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (reasonErr) setReasonErr(false); }}
            style={reasonErr ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 2px rgba(239,68,68,.15)' } : undefined}
          />
          {reasonErr ? <div style={{ fontSize: 10, color: 'var(--danger)', marginTop: 4 }}>Required — explain why this record is being corrected.</div> : null}
        </div>

        <div className="form-row" style={{ marginTop: 4 }}>
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            🕮 Audit trail
            <span style={{ color: 'var(--muted)', fontSize: 10, fontWeight: 400 }}>— prior corrections / deletions for this record</span>
          </label>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, maxHeight: 130, overflowY: 'auto' }}>
            {auditEntries.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', padding: 8 }}>No prior corrections on file</div>
            ) : auditEntries.map((a) => (
              <div key={a.id} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid rgba(30,42,64,.4)', fontSize: 11 }}>
                <span style={{ fontSize: 14 }}>{a.icon ?? '✏'}</span>
                <div style={{ flex: 1 }}>
                  <div>{prettyText(a.text)}</div>
                  <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 1 }}>
                    {new Date(a.time).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                    {a.userName ? ` · ${a.userName}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-danger btn-sm" onClick={() => { if (confirm('Delete this record? This is logged for audit.')) del.mutate(); }} disabled={del.isPending}>🗑 Delete</button>
          <div style={{ flex: 1 }}></div>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : '✓ Save Correction'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
